import { useMemo } from "react";
import { useCurrentWorkspaceMembers } from "@api/workspaceMember.api";
import { describeTaskActivityEvent } from "@common/constants/domains/agentRuns/taskActivityCopy";
import type { AgentRunSummary } from "@common/schemas/domains/agentRuns/agent-run.schema";
import type { TaskActivityEvent } from "@common/schemas/domains/agentRuns/task-activity.schema";
import { ActorType } from "@common/types/auditLog";
import { UserAvatar } from "@components/avatar/UserAvatar";
import { AgentActivityIcon } from "@components/presence/AgentWorkingActivity";
import { StateAwareLink } from "@components/routes/StateAwareLink";
import type { ActivityListItem } from "@design-system/ActivityList/ActivityList";
import { Avatar } from "@design-system/Avatar/Avatar";
import { useBoardCardTabStop } from "@design-system/Board/boardCardFocus";
import { Link } from "@design-system/Link/Link";
import { timeAgo } from "@helpers/date";
import { AgentRunStatusDot } from "./AgentRunStatusDot";
import type { AgentRunStatus } from "./agentRunStatus";

type Member = NonNullable<
    ReturnType<typeof useCurrentWorkspaceMembers>["data"]
>[number];
export type MembersById = ReadonlyMap<number, Member["user"]>;

interface UseMembersByIdOptions {
    /** Fetch only once the timeline is shown, so collapsed rows stay request-free. */
    enabled: boolean;
}

/** The scoped workspace's members by user id, for user-authored events. */
export function useMembersById({
    enabled,
}: UseMembersByIdOptions): MembersById {
    const { data: members } = useCurrentWorkspaceMembers({ enabled });
    return useMemo(
        () =>
            new Map(
                (members ?? []).map((member) => [member.user.id, member.user]),
            ),
        [members],
    );
}

export const UserActivityIcon = ({
    user,
}: {
    user: Member["user"] | undefined;
}) =>
    user === undefined ? (
        <Avatar name="Someone" size="xs" />
    ) : (
        <UserAvatar size="xs" aria-hidden user={user} />
    );

interface ToTaskActivityItemParams {
    event: TaskActivityEvent;
    taskTitle: string;
    membersById: MembersById;
    /** Links a waited-on task; `null` keeps every label plain text. */
    taskPathOf: ((taskId: string) => string) | null;
}

export function toTaskActivityItem({
    event,
    taskTitle,
    membersById,
    taskPathOf,
}: ToTaskActivityItemParams): ActivityListItem {
    const label = describeTaskActivityEvent(event, { taskTitle });
    return {
        id: event.id,
        icon:
            event.actorType === ActorType.AGENT ? (
                <AgentActivityIcon />
            ) : (
                <UserActivityIcon
                    user={
                        event.actorId === null
                            ? undefined
                            : membersById.get(event.actorId)
                    }
                />
            ),
        label:
            event.eventType === "waiting" && taskPathOf !== null ? (
                <Link
                    size="xs"
                    render={
                        <StateAwareLink
                            to={taskPathOf(event.metadata.waitedOnTaskId)}
                        />
                    }
                >
                    {label}
                </Link>
            ) : (
                label
            ),
        time: timeAgo(event.createdAt, { hideAgo: true }),
    };
}

/** The status line's link to the task a run waits on, a Tab stop in its board card. */
const WaitingTaskLink = ({
    to,
    children,
}: {
    to: string;
    children: string;
}) => {
    const tabStopProps = useBoardCardTabStop();
    return (
        <Link {...tabStopProps} size="xs" render={<StateAwareLink to={to} />}>
            {children}
        </Link>
    );
};

/** The live status line as the first timeline row, with the panel's status dot. */
export const getAgentRunStatusItem = ({
    run,
    status,
    detail,
    waitingTaskPath,
}: {
    run: AgentRunSummary;
    status: AgentRunStatus;
    detail: string;
    /** Links the line to the waited-on task; `null` keeps it plain text. */
    waitingTaskPath: string | null;
}): ActivityListItem => ({
    id: `status-${run.id}`,
    icon: <AgentActivityIcon />,
    label:
        waitingTaskPath === null ? (
            detail
        ) : (
            <WaitingTaskLink to={waitingTaskPath}>{detail}</WaitingTaskLink>
        ),
    trailing: <AgentRunStatusDot status={status} />,
});
